import { useEffect, useState } from "react"
import { Formik, Form, Field, ErrorMessage } from "formik"
import * as Yup from "yup"
import useSummarize from "../../hooks/useSummarize"

export const modelNames = {
  "gpt-4o": "GPT-4O",
  "structured-output": "GPT-4O-Structured-Output",
  "gpt-4o-mini": "GPT-4O-Mini",
  "gpt-o3-mini": "GPT-O3-Mini",
}

const models = Object.values(modelNames)

function PromptPlayground() {
  const [response, setResponse] = useState(null)
  const [responseType, setResponseType] = useState("AI Summary")

  const {
    mutateAsync: callAi,
    isPending: isAiCallPending,
    isError: isAiCallErroredOut,
    error: aiCallError,
  } = useSummarize(setResponse, responseType)

  const initialValues = {
    prompt:
      "Simplify this radiology report for a layman. Format your response in XHTML 1.0 Strict DTD.",
    text: "",
    model: models[0],
    jsonSchema: "",
  }

  const validationSchema = Yup.object({
    prompt: Yup.string().required("Prompt is required"),
    text: Yup.string().required("Report text is required"),
    model: Yup.string().required("Model selection is required"),
    jsonSchema: Yup.string().when([], {
      is: () => responseType === "Structured Output",
      then: (schema) =>
        schema
          .required("JSON schema is required for structured output")
          .test("valid-json", "Invalid JSON schema format", (value) => {
            if (!value) return false
            try {
              JSON.parse(value)
              return true
            } catch {
              return false
            }
          }),
      otherwise: (schema) => schema,
    }),
  })

  const handleSubmit = (values) => {
    const payload = {
      text: values.text,
      model: values.model,
      prompt: values.prompt,
      responseType,
    }

    if (responseType === "Structured Output" && values.jsonSchema) {
      payload.schema = JSON.parse(values.jsonSchema)
    }

    callAi(payload, {
      onSuccess: (data) => {
        setResponse(data)
      },
    })
  }

  const handleClear = (resetForm) => {
    resetForm()
    setResponse(null)
  }

  const getAllowStructuredOutput = (model) =>
    [
      modelNames["structured-output"],
      modelNames["gpt-4o-mini"],
      modelNames["gpt-o3-mini"],
    ].includes(model)

  useEffect(() => {
    // Auto-select response type based on model capabilities
    if (!getAllowStructuredOutput && responseType === "Structured Output") {
      setResponseType("AI Summary")
    }
  }, [responseType])

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, resetForm, errors, touched }) => {
          const allowStructuredOutput = getAllowStructuredOutput(values.model)

          return (
            <Form>
              <div className="flex flex-col items-start gap-3 mt-4">
                <Field name="model">
                  {({ field }) => (
                    <select
                      {...field}
                      className="border-2 border-gray-400 w-64 rounded-lg px-4 py-2 bg-white"
                      disabled={isAiCallPending}
                      onChange={(e) => {
                        setFieldValue("model", e.target.value)
                        // Auto-adjust response type when model changes
                        if (
                          !getAllowStructuredOutput(e.target.value) &&
                          responseType === "Structured Output"
                        ) {
                          setResponseType("AI Summary")
                        }
                      }}
                    >
                      {models.map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>

                <div className="flex flex-col space-y-4">
                  <span className="text-lg font-semibold">Response Type:</span>
                  <div className="flex items-center space-x-6">
                    <RadioButton
                      checked={responseType === "AI Summary"}
                      label="AI Summary"
                      value="AI Summary"
                      onChange={(e) => setResponseType(e.target.value)}
                    />
                    {allowStructuredOutput && (
                      <RadioButton
                        checked={responseType === "Structured Output"}
                        label="Structured Output"
                        value="Structured Output"
                        onChange={(e) => setResponseType(e.target.value)}
                      />
                    )}
                  </div>
                </div>

                {responseType === "Structured Output" && (
                  <div className="w-full">
                    <label
                      className="block text-sm font-medium text-gray-700 mb-2"
                      htmlFor="jsonSchema"
                    >
                      JSON Schema (required for structured output){" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <Field name="jsonSchema">
                      {({ field }) => (
                        <textarea
                          {...field}
                          className={`w-full h-32 p-4 border-2 rounded-lg ${
                            errors.jsonSchema && touched.jsonSchema
                              ? "border-red-400"
                              : "border-gray-400"
                          }`}
                          disabled={isAiCallPending}
                          placeholder='Enter JSON schema, e.g.: {"type": "object", "properties": {"summary": {"type": "string"}}}'
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      className="text-red-600 text-sm mt-1"
                      component="p"
                      name="jsonSchema"
                    />
                  </div>
                )}

                <div className="w-full">
                  <label
                    className="block text-sm font-medium text-gray-700"
                    htmlFor="prompt"
                  >
                    Prompt
                  </label>
                  <Field name="prompt">
                    {({ field }) => (
                      <textarea
                        {...field}
                        className={`w-full h-28 p-4 border-2 rounded-lg ${
                          errors.prompt && touched.prompt
                            ? "border-red-400"
                            : "border-gray-400"
                        }`}
                        disabled={isAiCallPending}
                        placeholder="Prompt"
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    className="text-red-600 text-sm mt-1"
                    component="p"
                    name="prompt"
                  />
                </div>
                <div className="w-full">
                  <label
                    className="block text-sm font-medium text-gray-700"
                    htmlFor="text"
                  >
                    Report
                  </label>
                  <Field name="text">
                    {({ field }) => (
                      <textarea
                        {...field}
                        className={`w-full h-96 p-4 border-2 rounded-lg ${
                          errors.text && touched.text
                            ? "border-red-400"
                            : "border-gray-400"
                        }`}
                        disabled={isAiCallPending}
                        placeholder="Report"
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    className="text-red-600 text-sm mt-1"
                    component="p"
                    name="text"
                  />
                </div>
                {isAiCallErroredOut && (
                  <p className="w-full text-center text-sm text-red-700">
                    {aiCallError?.message}
                  </p>
                )}
                <div className="flex items-center gap-2 self-center">
                  <button
                    className="bg-secondary hover:bg-secondary_dark text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isAiCallPending}
                    type="submit"
                  >
                    Get AI Response
                  </button>
                  <button
                    className="border-2 border-gray-500 text-gray-500 hover:bg-gray-300 px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isAiCallPending}
                    type="button"
                    onClick={() => handleClear(resetForm)}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
      {response && (
        <>
          <div className="mt-4 flex gap-3 flex-col bg-yellow-100 p-4 rounded-lg border-2 border-yellow-500 w-full md:w-1/2">
            <p>
              <strong>There are two tabs.</strong> See below for exact purpose
              of each tab.
            </p>
            <ul className="list-disc ml-4">
              <li className="mb-2">
                <strong>{`"AI Summary as XHTML"`}</strong> shows the formatted
                AI results as it will look like in DOCX. Formatting only works
                if prompt is set to return results as XHTML.
              </li>
              <li>
                Second tab<strong>{`"AI Raw Response"`}</strong> shows the
                actual text returned by AI without any formatting. In case AI
                returned Invalid XHTML you can see exact text in this tab
              </li>
            </ul>
          </div>
          <Tabs data={response} />
        </>
      )}
    </>
  )
}

const Tabs = ({ data }) => {
  const [activeTab, setActiveTab] = useState("preview")

  const handleTabClick = (tab) => {
    setActiveTab(tab)
  }

  return (
    <div className="w-full mx-auto mt-8">
      <div className="flex border-b">
        <button
          className={`flex-1 py-2 text-center ${
            activeTab === "preview"
              ? "border-b-2 border-blue-500 text-blue-500"
              : "text-gray-500"
          }`}
          onClick={() => handleTabClick("preview")}
        >
          AI Summary as XHTML
        </button>
        <button
          className={`flex-1 py-2 text-center ${
            activeTab === "raw"
              ? "border-b-2 border-blue-500 text-blue-500"
              : "text-gray-500"
          }`}
          onClick={() => handleTabClick("raw")}
        >
          AI Raw Response
        </button>
      </div>
      <div className="p-4">
        {activeTab === "preview" && (
          <div>
            {!data.isValidXHTML ? (
              <div className="w-full h-screen p-4 border-2 border-gray-400 rounded-lg mt-5">
                <p>Invalid XHTML Document.</p>
                <p className="mt-2">
                  Please see the other tab ${`"AI Raw Response"`} for exact text
                  returned by the AI.
                </p>
              </div>
            ) : (
              <iframe
                className="w-full h-screen p-4 border-2 border-gray-400 rounded-lg mt-5"
                srcDoc={data.text}
                title="response"
              />
            )}
          </div>
        )}
        {activeTab === "raw" && (
          <div className="w-full h-screen p-4 border-2 border-gray-400 rounded-lg mt-5">
            <pre>
              {(() => {
                try {
                  return (
                    data.text && JSON.stringify(JSON.parse(data.text), null, 2)
                  )
                } catch (error) {
                  return data.text
                }
              })()}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}

const RadioButton = ({ label, value, checked, onChange }) => (
  <label className="flex items-center space-x-2">
    <input
      checked={checked}
      className="form-radio h-4 w-4 text-blue-600"
      type="radio"
      value={value}
      onChange={onChange}
    />
    <span>{label}</span>
  </label>
)

export default PromptPlayground
